import React, { useCallback, useRef, useState } from 'react';
import type { Project } from '@jira/platform__project-types/src/project-types.tsx';
import ShareIcon from '@atlaskit/icon/core/share';
import { token } from '@atlaskit/tokens';
import { JiraSoftwareBoardAri } from '@atlassian/ari/jira-software/board';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import { useIntl } from '@atlassian/jira-intl';
import type { Board } from '@atlassian/jira-navigation-apps-sidebar-common/src/types.tsx';
import { PROJECT_SHARE_BUTTON_SCOPE } from '@atlassian/jira-project-share-button/src/constants.tsx';
import {
	ProjectShareContainer,
	useProjectShareButton,
} from '@atlassian/jira-project-share-button/src/controllers/index.tsx';
import { ThemedButton } from '@atlassian/jira-project-theme-components/src/ui/themed-button/ThemedButton.tsx';
import { integrationTypes } from '@atlassian/jira-share-button/src/constants.tsx';
import { ShareButton as JiraShareButton } from '@atlassian/jira-share-button/src/rll-async.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { useFireJpdRoadmapOnTimelineSpotlightTrigger } from '@atlassian/jira-roadmap-on-timeline-utils/src/utils.tsx';
import type {
	RenderCustomTriggerButton,
	CustomTriggerButtonProps,
} from '@atlassian/jira-share-button/src/common/types.tsx';
import { messages } from './messages.tsx';

type ShareProps = {
	data: Project | Board;
};

const ShareButtonInner = ({ data }: ShareProps) => {
	const cloudId = useCloudId();
	const { formatMessage } = useIntl();
	const isUserBoard = 'isProjectBoard' in data && data.isProjectBoard === false;

	const integrationType = integrationTypes.BOARD;

	const objectAri = isUserBoard
		? JiraSoftwareBoardAri.create({
				siteId: cloudId,
				boardId: String(data.id),
			}).toString()
		: JiraProjectAri.create({
				siteId: cloudId,
				projectId: String(data.id),
			}).toString();

	const [{ onDialogClose, poSpotlightShareButtonId }] = useProjectShareButton();

	const contentType = `${data.name}_${integrationType}`;

	const renderCustomTriggerButton: RenderCustomTriggerButton = useCallback(
		({ onClick, isSelected }: CustomTriggerButtonProps, { ...triggerProps }) => (
			<ThemedButton
				{...triggerProps}
				onClick={onClick}
				isSelected={isSelected}
				id="po-spotlight-share-button"
				data-vc="share-button"
				appearance={isVisualRefreshEnabled() ? 'default' : 'subtle'}
				iconBefore={
					<ShareIcon
						spacing="spacious"
						label={formatMessage(messages.shareFormTitle)}
						color={isSelected ? 'currentColor' : token('color.icon.subtle')}
					/>
				}
			/>
		),
		[formatMessage],
	);

	const [isFirstHovered, setIsFirstHovered] = useState(false);
	const shareButtonRef = useRef<HTMLDivElement>(null);

	const handleIconHover = useCallback(() => {
		setIsFirstHovered(true);
		shareButtonRef?.current?.focus();
	}, []);

	const fireJpdRoadmapOnTimelineSpotlightTrigger = useFireJpdRoadmapOnTimelineSpotlightTrigger();
	const handleShareDialogClose = useCallback(() => {
		fireJpdRoadmapOnTimelineSpotlightTrigger();
		onDialogClose?.();
	}, [fireJpdRoadmapOnTimelineSpotlightTrigger, onDialogClose]);

	return isFirstHovered ? (
		<div ref={shareButtonRef}>
			<JiraShareButton
				integrationType={integrationType}
				productId="jira"
				subProductId="jira-software"
				shareTitle={data.name}
				shareContentType={contentType}
				objectAri={objectAri}
				poSpotlightShareButtonId={poSpotlightShareButtonId}
				shareFormTitle={formatMessage(messages.shareFormTitle)}
				shareFormHelperMessage={formatMessage(messages.shareHelperMessage)}
				renderCustomTriggerButton={renderCustomTriggerButton}
				onDialogClose={handleShareDialogClose}
			/>
		</div>
	) : (
		<ThemedButton
			iconBefore={
				<ShareIcon
					spacing="spacious"
					label={formatMessage(messages.shareFormTitle)}
					color={isVisualRefreshEnabled() ? token('color.icon.subtle') : 'currentColor'}
				/>
			}
			appearance={isVisualRefreshEnabled() ? 'default' : 'subtle'}
			onMouseEnter={handleIconHover}
			onFocus={handleIconHover}
		/>
	);
};

export const ShareButton = ({ data }: ShareProps): React.JSX.Element => (
	<ProjectShareContainer scope={PROJECT_SHARE_BUTTON_SCOPE}>
		<ShareButtonInner data={data} />
	</ProjectShareContainer>
);
